import { Injectable } from '@angular/core';
import { forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import {
    keyBy as _keyBy,
    omit as _omit
} from 'lodash';
import {
    UserSetting,
    UserSettingsStateModel,
    userSettingsStateDefault,
    UserSettingDefinition,
    UserSettingDefinitionTypes
} from './user-settings.types';
import { UserSettingsFetchAll, UserSettingsSave, UserSettingsDelete } from './user-settings.actions';
import { CustomHttp } from './custom-http.service';
import { UserSettingsService } from './user-settings.service';

@Injectable()
@State<UserSettingsStateModel>({
    name: 'userSettings',
    defaults: userSettingsStateDefault,
})
export class UserSettingsState extends UserSettingsService {
    constructor(
        public http: CustomHttp,
        public store: Store
    ) {
        super(http);
    }

    @Action(UserSettingsFetchAll)
    private _fetchAllUserSettings({ patchState }: StateContext<UserSettingsStateModel>) {
        return forkJoin([
            this.fetchVirtualMetrics(),
            this.fetchUserSettingsDefinitions(),
            this.fetchUserSettings()
        ]).pipe(
            map(([virtualMetrics, userSettingDefinitions, userSettings]) => {
                return patchState({
                    virtualMetrics: virtualMetrics,
                    userSettingDefinitionsByName: _keyBy(userSettingDefinitions, def => def.name),
                    userSettingsById: _keyBy(userSettings, setting => setting.id)
                });
            })
        );
    }

    @Action(UserSettingsSave)
    private _saveUserSetting({ patchState }: StateContext<UserSettingsStateModel>, { userSettings }: UserSettingsSave) {
        const newUserSettings = [...userSettings];

        return this.saveUserSettings(newUserSettings).pipe(
            map(() => patchState({ userSettingsById: _keyBy(newUserSettings, setting => setting.id) })),
            switchMap(() => this.store.dispatch(new UserSettingsFetchAll()))
        );
    }

    @Action(UserSettingsDelete)
    private _deleteUserSetting({ patchState, getState }: StateContext<UserSettingsStateModel>, { userSettingId }: UserSettingsDelete) {
        const currentUserSettingsById = getState().userSettingsById;
        const filteredUserSettings = _omit(currentUserSettingsById, [userSettingId]);

        return this.deleteUserSettings(userSettingId).pipe(
            map(() => patchState({ userSettingsById: filteredUserSettings }))
        );
    }

    @Selector()
    static virtualMetrics(state: UserSettingsStateModel) {
        return state.virtualMetrics;
    }

    @Selector()
    static userSettingDefinitionsByName(state: UserSettingsStateModel): Record<string, UserSettingDefinition> {
        return state.userSettingDefinitionsByName;
    }

    @Selector()
    static userSettingsById(state: UserSettingsStateModel): Record<string, UserSetting> {
        return state.userSettingsById;
    }

    @Selector([
        UserSettingsState.userSettingsById,
        UserSettingsState.userSettingDefinitionsByName
    ])
    static pptContainerSetting(
        state: UserSettingsStateModel,
        userSettingsById: Record<string, UserSetting>,
        userSettingDefinitionsByName: UserSettingDefinition[]
    ): UserSetting {
        const pptContainerSettingDefinition = userSettingDefinitionsByName?.[UserSettingDefinitionTypes.pptContainerExpanded];
        const pptContainerSetting = userSettingsById?.[pptContainerSettingDefinition.id];

        return pptContainerSetting;
    }
}
